import ContentState, { StatePage } from "../../../Component/UI/ContentState";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import Layout_user from "../../../Component/UI/Layout_user";
import api2 from "../../../Component/axios_Interceptor/api2";
import api, { restoreSession } from "../../../Component/axios_Interceptor/api";
import url, { SERVER_ADDRESS } from "../../../Component/server";
import { LiveListItem } from "../../../Component/Interface/Live";
import LiveList from "./Component/LiveList";
import { EmptyLiveState, LiveSectionTitle, LiveTabs, LiveTab } from "./Component/LiveListPresentation";
import SellerFooter from "../../../Component/UI/SellerFooter";
import LoginModal from "../Sign/Modal/LoginModal";
import { GTM_EVENTS, pushGtmEvent } from "../../../Component/analytics/gtm";
import shareIcon from "../../../Image/ic_store_share.svg";
import inquiryIcon from "../../../Image/ic_store_inquiry.svg";
import BuyerFeatureIcon from "../../../Component/UI/BuyerFeatureIcon";

const resolveStoreImg = (img: string | undefined) => {
  if (!img?.trim()) return "";
  return /^(https?:|\/|data:)/.test(img) ? img : SERVER_ADDRESS + "openfile/" + img;
};
type LiveGroups = { normal: { broadcasting: LiveListItem[]; scheduled: LiveListItem[] }; secret: { broadcasting: LiveListItem[]; scheduled: LiveListItem[] } };

export default function SellerProfile() {
  const { seq } = useParams<{ seq: string }>();
  const reviewMode = new URLSearchParams(useLocation().search).get('payment_review') === '1';
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(false);
  const [loginModalVisible, setLoginModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [storeInfo, setStoreInfo] = useState<any>(null);
  const [lives, setLives] = useState<LiveGroups | null>(null);
  const [liveError, setLiveError] = useState("");
  const [liveTab, setLiveTab] = useState<"normal" | "secret">("normal");
  const [isDescExpanded, setIsDescExpanded] = useState(false);
  const [descOverflow, setDescOverflow] = useState(false);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const profileRequest = useRef(0);
  const followPending = useRef(false);
  const [following, setFollowing] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [sharing, setSharing] = useState(false);
  const sharePending = useRef(false);
  const shareRef = useRef<HTMLDivElement>(null);
  const [shareNotice, setShareNotice] = useState("");
  const [manualCopy, setManualCopy] = useState(false);
  const storePath = `/store/${seq}`;
  const storeLink = new URL(storePath, window.location.origin).href;

  const getStoreProfileData = useCallback(async () => {
    const request = ++profileRequest.current;
    setIsLoading(true);
    setLiveError("");
    try {
      const token = await restoreSession();
      if (request !== profileRequest.current) return;
      setIsLogin(Boolean(token));
      const client = token ? api : api2;
      const [profile, broadcasts] = await Promise.allSettled([
        client.get(url.Communication_url.store_public_profile, { params: { store_seq: Number(seq) } }),
        client.get(url.Communication_url.seller_profile_lives, { params: { store_seq: Number(seq), ...(reviewMode ? { payment_review: "1" } : {}) } }),
      ]);
      if (request !== profileRequest.current) return;
      if (profile.status !== "fulfilled" || Number(profile.value.data?.seq) !== Number(seq)) throw new Error("Invalid store profile response");
      setStoreInfo(profile.value.data);
      const data = broadcasts.status === "fulfilled" ? broadcasts.value.data : null;
      if ([data?.normal?.broadcasting, data?.normal?.scheduled, data?.secret?.broadcasting, data?.secret?.scheduled].every(Array.isArray)) {
        setLives(data);
      } else {
        setLives(null);
        setLiveError("방송 목록을 불러오지 못했습니다.");
      }
    } catch (error) {
      console.error(error);
      if (request !== profileRequest.current) return;
      setStoreInfo(null);
      setLives(null);
    } finally {
      if (request === profileRequest.current) setIsLoading(false);
    }
  }, [seq, reviewMode]);

  useEffect(() => {
    setStoreInfo(null);
    setLiveTab("normal");
    setIsDescExpanded(false);
    setShareOpen(false);
    setShareNotice("");
    setManualCopy(false);
    void getStoreProfileData();
    return () => { profileRequest.current++; };
  }, [getStoreProfileData]);

  useEffect(() => {
    const element = descriptionRef.current;
    if (!element || isDescExpanded) return;
    const measure = () => setDescOverflow(element.scrollHeight > element.clientHeight);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [storeInfo, isDescExpanded, isLoading]);

  useEffect(() => {
    if (!shareOpen) return;
    const close = (event: MouseEvent) => { if (!shareRef.current?.contains(event.target as Node)) setShareOpen(false); };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [shareOpen]);

  const handleFollow = async () => {
    if (!storeInfo || followPending.current) return;
    if (!isLogin) { setLoginModalVisible(true); return; }
    const followed = storeInfo.follow_yn === "y";
    const request = profileRequest.current;
    followPending.current = true;
    setFollowing(true);
    try {
      const result = followed
        ? await api.delete(url.Communication_url.store_unfollow, { data: { store_seq: Number(seq) } })
        : await api.post(url.Communication_url.store_follow, { store_seq: Number(seq) });
      if (result.data !== "성공") throw new Error("Follow update failed");
      void api.get(url.Communication_url.user_info).then(({ data: user }) => {
        if (user?.userid) pushGtmEvent(followed ? GTM_EVENTS.favoriteCancel : GTM_EVENTS.favoriteAdd, Number(seq), user.userid);
      }).catch(() => undefined);
      if (request === profileRequest.current) await getStoreProfileData();
    } catch (error) {
      console.error(error);
      if (request === profileRequest.current) {
        alert("팔로우 변경 결과를 확인하지 못했습니다. 현재 상태를 다시 불러옵니다.");
        await getStoreProfileData();
      }
    } finally {
      followPending.current = false;
      setFollowing(false);
    }
  };

  const handleShare = async (copy: boolean) => {
    if (sharePending.current) return;
    const request = profileRequest.current;
    sharePending.current = true;
    setSharing(true);
    setShareNotice("");
    setManualCopy(false);
    try {
      if (copy) {
        if (!navigator.clipboard?.writeText) throw new Error("Clipboard unavailable");
        await navigator.clipboard.writeText(storeLink);
        if (request === profileRequest.current) setShareNotice("스토어 링크를 복사했습니다. 카카오톡이나 문자에 붙여넣어 주세요.");
      } else {
        await navigator.share({ title: storeInfo.store_name, url: storeLink });
      }
      if (request === profileRequest.current) setShareOpen(false);
    } catch (error: any) {
      if (request !== profileRequest.current || error?.name === "AbortError") return;
      setShareNotice(copy ? "링크를 자동으로 복사하지 못했습니다. 표시된 주소를 선택해 복사해 주세요." : "공유 창을 열지 못했습니다. 링크를 복사해 전달해 주세요.");
      setManualCopy(copy);
      setShareOpen(true);
    } finally {
      sharePending.current = false;
      setSharing(false);
    }
  };

  if (isLoading) return <Layout_user background="white"><StatePage label="스토어" kind="loading" icon="store" title="스토어를 불러오는 중입니다." description="스토어와 라이브 정보를 확인하고 있어요." /></Layout_user>;
  if (!storeInfo) return <Layout_user background="white"><StatePage label="스토어" kind="error" icon="store" title="스토어를 불러오지 못했습니다." description="연결 상태를 확인한 뒤 다시 시도해 주세요.
다른 스토어를 찾아볼 수도 있어요." action={{ label: "다시 시도", onClick: getStoreProfileData }} secondaryAction={{ label: "스토어 찾기", href: "/search" }} /></Layout_user>;

  const renderLives = (items: LiveListItem[], isLive: boolean) => <section>
    <LiveSectionTitle scheduled={!isLive} />
    {items.length ? <Broadcasts><LiveList liveList={items} isLive={isLive} search_live={getStoreProfileData} isLogin={isLogin} hideStoreInfo /></Broadcasts>
      : <EmptyLiveState scheduled={!isLive} />}
  </section>;

  return <Layout_user background="white">
    <LoginModal visible={loginModalVisible} handleModal={() => setLoginModalVisible(false)} returnTo={storePath} />
    <ProfileWrapper>
      <CustomTopBar>
        <IconButton type="button" aria-label="뒤로가기" onClick={() => navigate(-1)}><img src={require("../../../Image/ic_left_white.png")} alt="" /></IconButton>
        <TopBarTitle>{storeInfo.store_name}</TopBarTitle>
        <ShareArea ref={shareRef} onKeyDown={event => { if (event.key === "Escape") { setShareOpen(false); shareRef.current?.querySelector<HTMLButtonElement>("button")?.focus(); } }}>
          <IconButton type="button" aria-label="스토어 링크 공유" aria-expanded={shareOpen} aria-controls="store-share-options" onClick={() => setShareOpen(!shareOpen)}>
            <img src={shareIcon} alt="" />
          </IconButton>
          {shareOpen && <ShareOptions id="store-share-options" aria-label="스토어 공유 방법">
            <p>스토어 링크를 카카오톡이나 문자로 전달해 보세요.</p>
            {typeof navigator.share === "function" && <ShareAction $primary type="button" disabled={sharing} onClick={() => handleShare(false)}><BuyerFeatureIcon name="share" /><span>카카오톡·문자로 공유</span></ShareAction>}
            <ShareAction type="button" disabled={sharing} onClick={() => handleShare(true)}><BuyerFeatureIcon name="link" /><span>링크 복사</span></ShareAction>
            {manualCopy && <input aria-label="복사할 스토어 주소" readOnly autoFocus value={storeLink} onFocus={event => event.target.select()} onClick={event => event.currentTarget.select()} />}
          </ShareOptions>}
        </ShareArea>
      </CustomTopBar>
      {resolveStoreImg(storeInfo.banner_img) ? <MainBanner src={resolveStoreImg(storeInfo.banner_img)} alt={`${storeInfo.store_name} 배너`} onError={event => { event.currentTarget.style.visibility = "hidden"; }} />
        : <EmptyBanner aria-label="등록된 스토어 배너가 없습니다." />}
      <ProfileContainer>
        <HeaderSection>
          <ProfileImg src={resolveStoreImg(storeInfo.profile_img) || require("../../../Image/Logo.png")} alt="" onError={event => { event.currentTarget.src = require("../../../Image/Logo.png"); }} />
          <StoreMeta><StoreName>{storeInfo.store_name}</StoreName><FollowStats>팔로우 {Number.isInteger(storeInfo.follow_count) && storeInfo.follow_count >= 0 ? storeInfo.follow_count.toLocaleString("ko-KR") : "확인 불가"}</FollowStats></StoreMeta>
          <FollowBtn type="button" disabled={following} $isFollowing={storeInfo.follow_yn === "y"} onClick={handleFollow}>{storeInfo.follow_yn === "y" ? "팔로잉" : "팔로우"}</FollowBtn>
        </HeaderSection>
        {storeInfo.store_intro && <DescSection>
          <DescText id="store-description" ref={descriptionRef} $isExpanded={isDescExpanded}>{storeInfo.store_intro}</DescText>
          {(descOverflow || isDescExpanded) && <MoreBtn type="button" aria-expanded={isDescExpanded} aria-controls="store-description" onClick={() => setIsDescExpanded(!isDescExpanded)}>{isDescExpanded ? "접기" : "...더보기"}</MoreBtn>}
        </DescSection>}
        {liveError ? <ContentState kind="error" icon="live" title={liveError} description="연결 상태를 확인한 뒤 다시 불러와 주세요." action={{ label: "다시 시도", onClick: getStoreProfileData }} /> : lives && <>
          <StoreLiveTabs role="tablist" aria-label="방송 구분">
            <LiveTab type="button" role="tab" id="normal-live-tab" aria-controls="seller-live-panel" aria-selected={liveTab === "normal"} $selected={liveTab === "normal"} onClick={() => setLiveTab("normal")}>공개 라이브</LiveTab>
            <LiveTab type="button" role="tab" id="secret-live-tab" aria-controls="seller-live-panel" aria-selected={liveTab === "secret"} $selected={liveTab === "secret"} onClick={() => setLiveTab("secret")}>시크릿 라이브</LiveTab>
          </StoreLiveTabs>
          <div role="tabpanel" id="seller-live-panel" aria-labelledby={liveTab === "normal" ? "normal-live-tab" : "secret-live-tab"}>
            {liveTab === "normal" ? <>
              {renderLives(lives.normal.broadcasting, true)}
              {renderLives(lives.normal.scheduled, false)}
            </> : storeInfo.follow_yn === "y" ? <>
              {renderLives(lives.secret.broadcasting, true)}
              {renderLives(lives.secret.scheduled, false)}
            </> : <ContentState kind="locked" icon="live" title="팔로우한 회원만 조회가능합니다." description="스토어를 팔로우하고 시크릿 라이브를 만나보세요." action={{ label: "팔로우하기", onClick: handleFollow }} />}
          </div>
        </>}
      </ProfileContainer>
      <FooterContainer><SellerFooter store={storeInfo} /></FooterContainer>
      <InquiryButton to={`/store/${seq}/inquiry`}><img src={inquiryIcon} alt="" />셀러 문의하기</InquiryButton>
      {shareNotice && <ShareNotice role="status">{shareNotice}</ShareNotice>}
    </ProfileWrapper>
  </Layout_user>;
}

const ProfileWrapper = styled.main`
  display: flex; flex-direction: column; min-height: var(--browser-layout-height, 100dvh); background: var(--user-surface); color: var(--user-text); position: relative;
  button:focus-visible, a:focus-visible { outline: 2px solid #7955f0; outline-offset: 3px; }
  button:disabled { opacity: .6; cursor: wait; }
`;
const CustomTopBar = styled.header`
  position: sticky; top: 0; z-index: 10; display: flex; align-items: center; gap: 8px;
  background: var(--user-header); border-bottom: 1px solid var(--user-border); height: 60px; margin-bottom: -60px; padding: 0 8px; box-sizing: border-box; width: 100%;
`;
const IconButton = styled.button`
  display: grid; place-items: center; flex-shrink: 0; width: 44px; height: 44px; background: none; border: 0; padding: 10px; cursor: pointer;
  img { width: 24px; height: 24px; object-fit: contain; filter: var(--user-icon-filter); }
`;
const TopBarTitle = styled.div`flex: 1; min-width: 0; color: var(--user-text); font-size: 18px; text-align: center; font-family: var(--font-Pretendard-SemiBold); overflow: hidden; text-overflow: ellipsis; white-space: nowrap;`;
const ShareArea = styled.div`position: relative;`;
const ShareOptions = styled.div`
  position: absolute; right: 0; top: 48px; width: min(280px, calc(100vw - 32px)); padding: 12px; box-sizing: border-box;
  background: var(--user-surface); color: var(--user-text); border-radius: 12px; box-shadow: 0 6px 24px #0003;
  p { margin: 2px 2px 12px; font-size: 13px; line-height: 1.6; color: var(--user-text-muted); word-break: keep-all; }
  input { box-sizing: border-box; width: 100%; margin-top: 12px; padding: 12px; border: 1px solid var(--user-border); border-radius: 8px; background: var(--user-surface-raised); color: var(--user-text); font-size: 16px; user-select: text; }
`;
const ShareAction = styled.button<{ $primary?: boolean }>`
  display: flex; align-items: center; justify-content: center; gap: 10px; width: 100%; min-height: 50px;
  margin-top: 8px; padding: 12px; border: 0; border-radius: 10px; font: 15px var(--font-Pretendard-SemiBold); line-height: 1.5; cursor: pointer; touch-action: manipulation;
  background: ${({ $primary }) => $primary ? 'var(--user-button)' : 'var(--user-accent-soft)'};
  color: ${({ $primary }) => $primary ? '#fff' : 'var(--user-accent)'};
  svg { flex-shrink: 0; }
  &:hover:not(:disabled) { filter: brightness(.94); }
  &:active:not(:disabled) { transform: translateY(1px); }
`;
const MainBanner = styled.img`display: block; width: 100%; aspect-ratio: 1; object-fit: cover; background: var(--user-surface-raised);`;
const EmptyBanner = styled.div`width: 100%; aspect-ratio: 1; background: var(--user-surface-raised);`;
const ProfileContainer = styled.div`padding: 10px 16px 40px;`;
const HeaderSection = styled.div`display: flex; align-items: center; gap: 12px;`;
const ProfileImg = styled.img`width: 40px; height: 40px; flex-shrink: 0; border-radius: 50%; object-fit: cover; border: 1px solid var(--user-border);`;
const StoreMeta = styled.div`display: flex; flex-direction: column; gap: 2px; flex: 1; min-width: 0;`;
const StoreName = styled.h1`margin: 0; font-size: 14px; line-height: 20px; font-family: var(--font-Pretendard-SemiBold); overflow-wrap: anywhere;`;
const FollowStats = styled.div`font-size: 12px; line-height: 16px; color: var(--user-text-muted);`;
const FollowBtn = styled.button<{ $isFollowing: boolean }>`
  flex-shrink: 0; width: 80px; min-height: 40px; border-radius: 8px; font-size: 16px; font-family: var(--font-Pretendard-SemiBold); cursor: pointer;
  background: ${({ $isFollowing }) => $isFollowing ? "var(--user-surface-raised)" : "linear-gradient(135deg,#7955f0,#3209b9)"};
  color: ${({ $isFollowing }) => $isFollowing ? "var(--user-text)" : "white"}; border: 0;
`;
const DescSection = styled.div`margin-top: 16px;`;
const DescText = styled.div<{ $isExpanded: boolean }>`
  font-size: 14px; line-height: 1.4; white-space: pre-wrap; overflow-wrap: anywhere;
  ${({ $isExpanded }) => !$isExpanded && "display: -webkit-box; -webkit-line-clamp: 4; -webkit-box-orient: vertical; overflow: hidden;"}
`;
const MoreBtn = styled.button`display: block; min-height: 40px; padding: 6px 0; border: 0; background: transparent; color: var(--user-text-muted); font: inherit; font-size: 12px; cursor: pointer;`;
const StoreLiveTabs = styled(LiveTabs)`margin-top: 30px;`;
const Broadcasts = styled.div`
  margin-top: 10px;
  > div { display: grid; grid-auto-flow: column; grid-template-columns: none; grid-auto-columns: minmax(140px, 156px); justify-content: start; gap: 8px; margin: 0; padding: 0 0 8px; overflow-x: auto; }
`;
const FooterContainer = styled.footer`margin-top: auto; padding-bottom: calc(112px + env(safe-area-inset-bottom)); background: var(--user-surface-muted);`;
const InquiryButton = styled(Link)`
  position: fixed; bottom: calc(32px + env(safe-area-inset-bottom)); left: 50%; transform: translateX(-50%); z-index: 5;
  display: flex; align-items: center; justify-content: center; gap: 6px; min-width: 140px; height: 48px; padding: 0 18px; box-sizing: border-box; border-radius: 24px;
  background: #2d2d2d; color: white; text-decoration: none; box-shadow: 0 4px 8px #0002; font-size: 14px; font-family: var(--font-Pretendard-SemiBold);
  img { width: 20px; height: 20px; } &:hover { background: #444; }
`;
const ShareNotice = styled.div`
  position: fixed; z-index: 6; bottom: calc(84px + env(safe-area-inset-bottom)); left: 50%; transform: translateX(-50%);
  width: max-content; max-width: min(440px, calc(100vw - 32px)); box-sizing: border-box; padding: 12px 16px;
  border-radius: 8px; background: #2d2d2d; color: white; font-size: 13px; line-height: 1.5; box-shadow: 0 4px 12px #0003;
`;
